/**
 * Design System - ChatInputMobile Component
 *
 * Mobile-specific chat input component with inline buttons
 * Buttons appear on the same line as placeholder/text
 * 100% Design System Integration - NO hardcoded values
 */

import React from 'react';
import {
  typographyColors,
  backgroundColors,
  brandColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  strokeColors,
} from '../../utils';

export interface ChatInputMobileProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: () => void;
  onMicClick?: () => void;
  onStopClick?: () => void;
  onAttachClick?: () => void;
  showMicrophone?: boolean;
  showAttachment?: boolean;
  disclaimerText?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  isRecording?: boolean;
  recordingTime?: number;
  inputFontSize?: string;
}

export const ChatInputMobile = React.forwardRef<
  HTMLDivElement,
  ChatInputMobileProps
>(
  (
    {
      placeholder = 'Ask anything about travel.',
      value = '',
      onChange,
      onSubmit,
      onMicClick,
      onStopClick,
      onAttachClick,
      showMicrophone = false,
      showAttachment = false,
      disclaimerText = 'Nexgo is AI-powered and may not always be accurate. Verify when needed.',
      className,
      disabled = false,
      isLoading = false,
      isRecording = false,
      inputFontSize,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState(value);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    // Sync internal state with value prop
    React.useEffect(() => {
      setInputValue(value);
    }, [value]);

    // Auto-resize textarea based on content
    React.useEffect(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const maxHeight = 120; // Mobile max height
        textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
      }
    }, [inputValue]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onChange?.(newValue);
    };

    const handleSubmit = () => {
      if (inputValue.trim() && !disabled && !isLoading) {
        onSubmit?.();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    return (
      <div
        ref={ref}
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
        {...props}
      >
        <div
          style={{
            backgroundColor: 'var(--Colors-Background-primary, #FFF)',
            borderColor: 'var(--Colors-Stroke-Base, #D6D3D1)',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: 'var(--border-radius-rounded-lg, 8px)',
            boxShadow: '4px 4px 16px 0 rgba(0, 0, 0, 0.06)',
            padding:
              'var(--spacing-padding-margins-2, 8px) var(--spacing-padding-margins-2, 8px) var(--spacing-padding-margins-2, 8px) var(--spacing-padding-margins-3, 12px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--spacing-padding-margins-3, 12px)',
            alignSelf: 'stretch',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {/* Mobile Layout: Inline buttons with input */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing[2],
              width: '100%',
              minHeight: '32px',
            }}
          >
            {/* Left side: Attachment */}
            {showAttachment && (
              <button
                onClick={disabled || isLoading ? undefined : onAttachClick}
                disabled={disabled || isLoading}
                style={{
                  backgroundColor: backgroundColors.primary,
                  borderColor: strokeColors.light,
                  borderWidth: borders.width.thin,
                  borderStyle: 'solid',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: borders.radius.full,
                  cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: disabled || isLoading ? 0.5 : 1,
                  flexShrink: 0,
                }}
                onMouseEnter={e => {
                  if (!disabled && !isLoading) {
                    e.currentTarget.style.backgroundColor =
                      backgroundColors.secondary;
                  }
                }}
                onMouseLeave={e => {
                  if (!disabled && !isLoading) {
                    e.currentTarget.style.backgroundColor =
                      backgroundColors.primary;
                  }
                }}
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  style={{
                    width: '20px',
                    height: '20px',
                    flexShrink: 0,
                    aspectRatio: '1/1',
                  }}
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M5.404 14.596C4.6481 13.8402 4.09028 12.9096 3.77995 11.8866C3.46961 10.8637 3.41635 9.78005 3.62488 8.73163C3.8334 7.6832 4.29728 6.70239 4.97542 5.87607C5.65355 5.04975 6.52502 4.40343 7.51261 3.99437C8.50021 3.5853 9.57344 3.42612 10.6373 3.53092C11.7011 3.63572 12.7226 4.00127 13.6114 4.59518C14.5002 5.18909 15.2288 5.99304 15.7326 6.9358C16.2365 7.87856 16.5001 8.93104 16.5 10C16.5 10.3315 16.3683 10.6495 16.1339 10.8839C15.8995 11.1183 15.5815 11.25 15.25 11.25C14.9185 11.25 14.6005 11.1183 14.3661 10.8839C14.1317 10.6495 14 10.3315 14 10C13.9999 9.03256 13.6492 8.09794 13.0128 7.36926C12.3765 6.64057 11.4976 6.16722 10.539 6.03687C9.58033 5.90652 8.60696 6.12801 7.79913 6.66031C6.9913 7.19261 6.40376 7.99965 6.14534 8.93194C5.88693 9.86423 5.97515 10.8586 6.39366 11.7308C6.81218 12.603 7.53263 13.294 8.42156 13.6758C9.31049 14.0576 10.3077 14.1042 11.2283 13.8071C12.149 13.51 12.9309 12.8893 13.429 12.06C13.8257 12.4106 14.3152 12.6392 14.8386 12.7184C15.3621 12.7976 15.8972 12.724 16.3799 12.5065C16.8626 12.2889 17.2722 11.9367 17.5595 11.4921C17.8469 11.0475 17.9999 10.5294 18 10C17.9999 8.14916 17.3581 6.3556 16.1839 4.92492C15.0097 3.49425 13.3757 2.51497 11.5605 2.15395C9.74517 1.79293 7.86086 2.07251 6.2286 2.94504C4.59633 3.81757 3.3171 5.22908 2.60887 6.93905C1.90064 8.64902 1.80723 10.5517 2.34455 12.3228C2.88187 14.0939 4.01668 15.6239 5.55563 16.6522C7.09457 17.6804 8.94243 18.1432 10.7844 17.9617C12.6263 17.7803 14.3483 16.9658 15.657 15.657C15.7895 15.5148 15.8616 15.3268 15.8582 15.1325C15.8547 14.9382 15.776 14.7528 15.6386 14.6154C15.5012 14.478 15.3158 14.3993 15.1215 14.3958C14.9272 14.3924 14.7392 14.4645 14.597 14.597C13.9934 15.2007 13.2768 15.6795 12.4881 16.0062C11.6995 16.333 10.8542 16.5011 10.0005 16.5011C9.14684 16.5011 8.30153 16.333 7.51286 16.0062C6.72419 15.6795 6.00759 15.2007 5.404 14.597M10 7.5C9.33696 7.5 8.70108 7.76339 8.23223 8.23223C7.76339 8.70107 7.5 9.33695 7.5 10C7.5 10.663 7.76339 11.2989 8.23223 11.7678C8.70108 12.2366 9.33696 12.5 10 12.5C10.663 12.5 11.2989 12.2366 11.7678 11.7678C12.2366 11.2989 12.5 10.663 12.5 10C12.5 9.33695 12.2366 8.70107 11.7678 8.23223C11.2989 7.76339 10.663 7.5 10 7.5Z'
                    fill='#A8A29E'
                  />
                </svg>
              </button>
            )}

            {/* Center: Input area */}
            <div
              style={{
                flex: 1,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                minHeight: '32px',
              }}
            >
              {/* Placeholder */}
              {!inputValue.trim() && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    fontFamily: 'var(--Typography-Fonts-Primary, Gantari)',
                    fontSize: 'var(--font-size-text-sm, 14px)',
                    fontStyle: 'normal',
                    fontWeight: 'var(--font-weight-font-normal, 400)',
                    lineHeight: 'normal',
                    color: 'var(--Colors-Typography-placeholder, #78716C)',
                    textAlign: 'left',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {placeholder}
                </div>
              )}

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={disabled || isLoading}
                placeholder=''
                style={{
                  fontFamily: fontPrimary,
                  fontWeight: typography.weight.regular,
                  fontSize: inputFontSize || typography.size.sm,
                  lineHeight: 'normal',
                  color: typographyColors.primary,
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  minHeight: '24px',
                  height: '24px',
                  maxHeight: '120px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  resize: 'none',
                  padding: 0,
                  margin: 0,
                  opacity: disabled || isLoading ? 0.6 : 1,
                }}
                rows={1}
              />
            </div>

            {/* Right side: Microphone/Stop + Send */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing[2],
                flexShrink: 0,
              }}
            >
              {/* Microphone/Stop Button */}
              {showMicrophone && (
                <button
                  onClick={
                    isRecording
                      ? disabled || isLoading
                        ? undefined
                        : onStopClick || onMicClick
                      : disabled || isLoading
                        ? undefined
                        : onMicClick
                  }
                  disabled={disabled || isLoading}
                  style={{
                    backgroundColor: isRecording
                      ? 'transparent'
                      : backgroundColors.primary,
                    borderColor: isRecording
                      ? 'transparent'
                      : strokeColors.light,
                    borderWidth: isRecording ? 0 : borders.width.thin,
                    borderStyle: isRecording ? 'none' : 'solid',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: borders.radius.full,
                    cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: disabled || isLoading ? 0.5 : 1,
                    flexShrink: 0,
                  }}
                  onMouseEnter={e => {
                    if (!isRecording && !disabled && !isLoading) {
                      e.currentTarget.style.backgroundColor =
                        backgroundColors.secondary;
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isRecording && !disabled && !isLoading) {
                      e.currentTarget.style.backgroundColor =
                        backgroundColors.primary;
                    }
                  }}
                >
                  {isRecording ? (
                    /* Wave Animation */
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '2px',
                        width: '20px',
                        height: '20px',
                      }}
                    >
                      {[0, 1, 2, 3, 4].map(i => (
                        <div
                          key={i}
                          style={{
                            width: '2px',
                            height: '12px',
                            backgroundColor: typographyColors.primary,
                            borderRadius: '1px',
                            animation: `wave 0.9s ease-in-out infinite`,
                            animationDelay: `${i * 0.1}s`,
                            transformOrigin: 'center bottom',
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    /* Microphone Icon */
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      style={{
                        width: '20px',
                        height: '20px',
                        flexShrink: 0,
                        aspectRatio: '1/1',
                      }}
                    >
                      <path
                        d='M13.3334 5.00001C13.3334 3.15906 11.841 1.66667 10.0001 1.66667C8.15913 1.66667 6.66675 3.15906 6.66675 5.00001V9.16667C6.66675 11.0076 8.15913 12.5 10.0001 12.5C11.841 12.5 13.3334 11.0076 13.3334 9.16667V5.00001Z'
                        fill='#A8A29E'
                      />
                      <path
                        d='M4.16675 9.16667C4.16675 9.93272 4.31763 10.6913 4.61078 11.399C4.90394 12.1067 5.33362 12.7498 5.87529 13.2915C6.41697 13.8331 7.06003 14.2628 7.76776 14.556C8.47549 14.8491 9.23404 15 10.0001 15C10.7661 15 11.5247 14.8491 12.2324 14.556C12.9401 14.2628 13.5832 13.8331 14.1249 13.2915C14.6665 12.7498 15.0962 12.1067 15.3894 11.399C15.6825 10.6913 15.8334 9.93272 15.8334 9.16667M10.0001 17.5V15.8333'
                        stroke='#A8A29E'
                        strokeWidth='1.66667'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </button>
              )}

              {/* Send Button */}
              <button
                onClick={handleSubmit}
                disabled={!inputValue.trim() || disabled || isLoading}
                style={{
                  backgroundColor: brandColors.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: borders.radius.sm,
                  cursor:
                    inputValue.trim() && !disabled && !isLoading
                      ? 'pointer'
                      : 'not-allowed',
                  opacity:
                    inputValue.trim() && !disabled && !isLoading ? 1 : 0.5,
                  transition: 'all 0.2s',
                  border: 'none',
                  flexShrink: 0,
                }}
                onMouseEnter={e => {
                  if (inputValue.trim() && !disabled && !isLoading) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={e => {
                  if (inputValue.trim() && !disabled && !isLoading) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M10.0001 17.5C9.75141 17.5 9.51295 17.4012 9.33714 17.2254C9.16132 17.0496 9.06255 16.8111 9.06255 16.5625V5.7L5.03755 9.725C4.85983 9.8906 4.62478 9.98075 4.3819 9.97647C4.13902 9.97218 3.90729 9.87379 3.73553 9.70203C3.56376 9.53026 3.46537 9.29853 3.46108 9.05565C3.4568 8.81278 3.54695 8.57772 3.71255 8.4L9.33755 2.775C9.51333 2.59944 9.75161 2.50082 10.0001 2.50082C10.2485 2.50082 10.4868 2.59944 10.6626 2.775L16.2876 8.4C16.4532 8.57772 16.5433 8.81278 16.539 9.05565C16.5347 9.29853 16.4363 9.53026 16.2646 9.70203C16.0928 9.87379 15.8611 9.97218 15.6182 9.97647C15.3753 9.98075 15.1403 9.8906 14.9626 9.725L10.9376 5.7V16.5625C10.9376 16.8111 10.8388 17.0496 10.663 17.2254C10.4871 17.4012 10.2487 17.5 10.0001 17.5Z'
                    fill={typographyColors.primary}
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        {disclaimerText && (
          <p
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.xs,
              fontWeight: typography.weight.regular,
              color: typographyColors.tertiary,
              textAlign: 'center',
            }}
          >
            {disclaimerText}
          </p>
        )}

        {/* Animation styles */}
        <style jsx>{`
          @keyframes wave {
            0%,
            100% {
              transform: scaleY(0.3);
              opacity: 0.7;
            }
            50% {
              transform: scaleY(1);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }
);

ChatInputMobile.displayName = 'ChatInputMobile';
